import { compare } from 'compare-versions'
import http from '@/services/lego'

interface Node {
  id: string
  pid: string
  render: string
  config: Record<string, any>
}

interface NodeResponse {
  data: any
  error: null | Error
  node: Node
}

type Content = Record<string, Node>

export function generateContentWithVersion(content: Content) {
  const version = content?.root?.config?.templateVersion
  if (!version || compare(version, '2.0.0', '<=')) {
    return content
  }
  return Object.keys(content).reduce((init, key) => {
    const render = content[key]?.render
    let node
    switch (render) {
      case 'ProductList':
        node = generateProducts(content[key])
        break
      case 'CombinationList':
        node = generateCombinations(content[key])
        break
      default:
        node = content[key]
    }
    init[key] = node
    return init
  }, {})
}

function generateProducts(node: Node): Node {
  if (!node) {
    return node
  }
  const { productRow, products } = node.config
  const length = products?.length
  let count = length
  switch (productRow) {
    case 'row':
      count = 2
      break
    case 1:
    case 2:
      count = 4
      break
    case 3:
      count = 6
  }
  return {
    ...node,
    config: {
      ...node.config,
      quantity: length,
      products: node.config.products?.slice(0, count),
    },
  }
}

function generateCombinations(node: Node): Node {
  if (!node) {
    return node
  }
  const combinations = node.config?.combinations
  return {
    ...node,
    config: {
      ...node.config,
      combinations: combinations?.slice(0, 3),
      quantity: combinations?.length,
    },
  }
}

export function serializeContentToView(content: Content) {
  const updateQueue = getNeedUpdateComponents(content)
  const domain = getDomain(content)
  const version = content?.root?.config?.templateVersion
  return new Promise(async resolve => {
    if (!updateQueue.length || (version && compare(version, '2.0.0', '<='))) {
      return resolve(content)
    }
    const nodes = await concurrencyPromise(updateQueue, domain)
    const updateContent = nodes.reduce(
      (init, node) => {
        const { render, id } = node.node
        let updateNode: Node | null
        switch (render) {
          case 'ProductList':
            updateNode = generateProductsList(node)
            break
          case 'CombinationList':
            updateNode = generateCombinationList(node)
            break
          default:
            updateNode = node.node
        }
        init[id] = updateNode
        return init
      },
      { ...content }
    )
    resolve(updateContent)
  })
}

function createPromiseWithoutReject(
  promiseHandler: (config: Node, domain?: string) => Promise<NodeResponse>
) {
  return (node: Node, domain?: string): Promise<NodeResponse> => {
    return new Promise(async resolve => {
      try {
        const { data } = await promiseHandler(node, domain)
        resolve({ data: data || null, error: null, node })
      } catch (error) {
        resolve({ data: null, error: error as Error, node })
      }
    })
  }
}

function getNeedUpdateComponents(content: Content) {
  return Object.values(content).filter(node =>
    ['ProductList', 'CombinationList'].includes(node.render)
  )
}

async function concurrencyPromise(
  nodes: Node[],
  domain: string | undefined,
  responseList: NodeResponse[] = []
) {
  if (nodes?.length) {
    const data = await Promise.all(
      nodes.splice(0, 5).map(node => createPromiseWithoutReject(generatePromise)(node, domain))
    )
    responseList.push(...data)
    if (nodes?.length) {
      await concurrencyPromise(nodes, domain, responseList)
    }
  }
  return responseList
}

function generatePromise(node: Node, domain?: string) {
  const { render, config = {} } = node
  switch (render) {
    case 'ProductList':
      const { parseType } = config
      if (parseType === 'tag') {
        return http.searchProducts(getProductSearchQuery(node), domain)
      } else if (parseType === 'id') {
        const ids = config?.ids
        if (ids?.length) {
          return http.getProductsList(ids)
        }
      }
      return Promise.resolve()
    case 'CombinationList':
      const ids = node.config?.ids
      return ids?.length ? http.getCombinationInfo(ids) : Promise.resolve()
    default:
      return Promise.resolve()
  }
}

export const getProductSearchQuery = (componentConfig: Node): Record<string, any> => {
  const {
    tags,
    merchantList,
    brandList,
    discountRange,
    priceList,
    categoryIds,
    excludedBrands,
    excludedCategories,
    labels,
    excludedTags,
    sortType,
    promoId,
    q,
    excludeLabels,
  } = componentConfig.config || {}
  return {
    id: componentConfig.id,
    tg: tags || [],
    ntg: excludedTags || [],
    m: merchantList,
    b: brandList,
    dr: discountRange ? `${(10 - discountRange[1]) * 10}-${(10 - discountRange[0]) * 10}` : null,
    prs: priceList ? `${priceList[0] * 100}-${priceList[1] * 100}` : null,
    cids: categoryIds,
    nb: excludedBrands,
    ncids: excludedCategories,
    l2: labels,
    f: 0,
    t: componentConfig.config.maxProductNum || 20,
    s: sortType || 'sales',
    promoId: promoId,
    q,
    nl: excludeLabels,
  }
}

function getDomain(content: Content) {
  const channel = content?.root?.config?.channel
  return channel ? `https://santa-${channel}.santasmartnetwork.com` : ''
}

function generateCombinationList(dataSource: NodeResponse) {
  const { data, error, node } = dataSource
  if (!data || error) {
    return node
  }
  return {
    ...node,
    config: {
      ...node.config,
      combinations: data.waterDrops || [],
      quantity: data.waterDrops?.length || 0,
    },
  }
}

function generateProductsList(dataSource: NodeResponse) {
  const { data, error, node } = dataSource
  if (!data || error) {
    return node
  }
  const { parseType, filterSellOut } = node.config || {}
  let products: Record<string, unknown>[] = []
  if (parseType === 'tag') {
    products = data.products.reduce((init, item) => {
      if (item?.type === 'PRODUCT') {
        const pd = item.searchProduct
        if (filterSellOut) {
          pd.product.inventoryStatus === 'AVAILABLE' && init.push(pd)
        } else {
          init.push(pd)
        }
      }
      return init
    }, [] as Record<string, unknown>[])
  } else if (parseType === 'id') {
    products = filterSellOut
      ? data.filter((pd: any) => pd.product.inventoryStatus === 'AVAILABLE')
      : data
  }
  return {
    ...node,
    config: {
      ...node.config,
      products,
      quantity: products.length,
    },
  }
}
